import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const IntentAnalyzerViewResult = ({result}) => {
  const getIntentColor = (category) => {
    switch(category?.toLowerCase()) {
      case 'information_sharing':
        return { bg: 'bg-info/20', text: 'text-info', icon: 'Info' };
      case 'demonstration':
        return { bg: 'bg-success/20', text: 'text-success', icon: 'PresentationChart' };
      case 'cost_benefit_analysis':
        return { bg: 'bg-warning/20', text: 'text-warning', icon: 'ChartLineUp' };
      default:
        return { bg: 'bg-base-200', text: 'text-base-content', icon: 'Question' };
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'bg-success/20 text-success';
    if (score >= 0.6) return 'bg-warning/20 text-warning';
    return 'bg-error/20 text-error';
  };

  const getLevelColor = (level) => {
    switch(level) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Primary Intent Section */}
        <div className="space-y-4">
          {/* Primary Intent Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className={`p-2 rounded-lg ${getIntentColor(result?.intent_analysis?.primary_intent?.category).bg}`}>
                  {PhosphorIcons[getIntentColor(result?.intent_analysis?.primary_intent?.category).icon] && 
                    React.createElement(PhosphorIcons[getIntentColor(result?.intent_analysis?.primary_intent?.category).icon], {
                      size: 20,
                      weight: "bold",
                      className: getIntentColor(result?.intent_analysis?.primary_intent?.category).text
                    })
                  }
                </div>
                <h3 className="card-title text-lg">Primary Intent</h3>
              </div>

              <div className="space-y-3">
                {/* Intent Category & Subtype */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium capitalize">
                      {result?.intent_analysis?.primary_intent?.category?.replace(/_/g, ' ')}
                    </span>
                    <div className="flex gap-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getConfidenceColor(result?.intent_analysis?.primary_intent?.confidence)}`}>
                        {Math.round(result?.intent_analysis?.primary_intent?.confidence * 100)}% confidence
                      </span>
                    </div>
                  </div>
                  <span className="text-sm text-base-content/70 capitalize">
                    Subtype: {result?.intent_analysis?.primary_intent?.subtype?.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Evidence */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Evidence</h4>
                  <div className="space-y-2">
                    {result?.intent_analysis?.primary_intent?.evidence?.map((item, index) => (
                      <div key={index} className="text-sm bg-base-300 p-2 rounded">
                        "{item}"
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Words */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <h4 className="font-medium mb-2">Action Words</h4>
                  <div className="flex flex-wrap gap-2">
                    {result?.intent_analysis?.primary_intent?.action_words?.map((word, index) => (
                      <span key={index} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Intents & Indicators Section */}
        <div className="space-y-4">
          {/* Secondary Intents Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.ListPlus size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Secondary Intents</h3>
              </div>

              <div className="space-y-3">
                {result?.intent_analysis?.secondary_intents?.map((intent, index) => (
                  <div key={index} className="p-3 bg-base-200 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium capitalize">
                        {intent.category?.replace(/_/g, ' ')}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getConfidenceColor(intent.confidence)}`}>
                        {Math.round(intent.confidence * 100)}%
                      </span>
                    </div>
                    <span className="text-sm text-base-content/70 block mb-2 capitalize">
                      Subtype: {intent.subtype?.replace(/_/g, ' ')}
                    </span>
                    <div className="text-sm bg-base-300 p-2 rounded">
                      "{intent.evidence?.[0]}"
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Intent Indicators Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.Gauge size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Analysis Quality</h3>
              </div>

              <div className="space-y-3">
                {/* Confidence Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Overall Confidence</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getConfidenceColor(result?.metadata?.intent_confidence?.overall_score)}`}>
                      {Math.round(result?.metadata?.intent_confidence?.overall_score * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${result?.metadata?.intent_confidence?.overall_score * 100}%`}}
                    ></div>
                  </div>
                </div>

                {/* Evidence Strength */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Evidence Strength</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getLevelColor(result?.metadata?.intent_confidence?.evidence_strength)}`}>
                      {result?.metadata?.intent_confidence?.evidence_strength}
                    </span>
                  </div>
                </div>

                {/* Ambiguity Level */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Ambiguity Level</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getLevelColor(result?.metadata?.intent_confidence?.ambiguity_level)}`}>
                      {result?.metadata?.intent_confidence?.ambiguity_level}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntentAnalyzerViewResult;